import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { syncedWorktree } from '@/lib/desktop-shell/conversation-worktree';
import type { DesktopShellStore, SyncedThread } from '@/lib/desktop-shell/store';
import { getLocalConversation } from '@/lib/local-conversations';
import { isLocalMachine } from '@/lib/local-runtime';
import { localFirstUniqueById } from './use-workspace-sync-bootstrap';

/** Conversations being read for the sidebar, not to read them twice. */
const joining = new Set<string>();

/**
 * A conversation the server started without the page (an app's task, one the assistant handed
 * work to) joins the sidebar as the first listing would have shown it: this machine's server
 * keeps its own, the cloud's listing the others.
 */
export async function joinSidebar(store: DesktopShellStore, id: string): Promise<boolean> {
  if (joining.has(id) || store.getState().syncedThreads.some((thread) => thread.id === id))
    return false;
  joining.add(id);
  try {
    const local = isLocalMachine() ? await getLocalConversation(id) : null;
    const state = store.getState();
    if (local !== null) {
      const workspace = state.syncedWorkspaces.find((entry) => entry.id === local.workspaceId);
      const workspaceId = local.workspaceId;
      if (workspace === undefined)
        state.setSyncedWorkspaces((current) =>
          localFirstUniqueById([
            ...current,
            {
              id: workspaceId,
              logicalWorkspaceId: local.logicalWorkspaceId ?? workspaceId,
              availableOnDevice: true,
              runtime: local.runtime,
              rootPath: local.workspaceRoot,
              name: local.workspaceName,
            },
          ]),
        );
      state.setSyncedAgentConfigs((current) =>
        localFirstUniqueById([
          ...current,
          { id: local.agentConfigId, registryId: local.agent.id, command: local.agent.command },
        ]),
      );
      add(store, {
        id: local.id,
        runtime: local.runtime,
        workspaceId,
        workspaceRoot: local.workspaceRoot,
        worktree: syncedWorktree(local.worktree),
        pullRequest: local.pullRequest ?? null,
        pullRequestBindings: local.pullRequestBindings ?? [],
        agentConfigId: local.agentConfigId,
        title: local.title,
        collaboration: local.collaboration,
        modelId: local.modelId,
        updatedAt: local.updatedAt,
        lastMessageAt: local.lastMessageAt,
        messageRevision: local.messageRevision,
        tags: local.tags,
      });
      return true;
    }
    const listed = await apiFetch<{ threads: Omit<SyncedThread, 'runtime'>[] }>(
      '/api/sync/bootstrap',
      { credentials: 'include' },
    );
    const thread = listed.threads.find((entry) => entry.id === id);
    if (thread === undefined) return false;
    add(store, { ...thread, runtime: 'cloud' });
    return true;
  } catch {
    return false;
  } finally {
    joining.delete(id);
  }
}

function add(store: DesktopShellStore, thread: SyncedThread): void {
  store
    .getState()
    .setSyncedThreads((current) =>
      localFirstUniqueById([thread, ...current]).sort(
        (left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
      ),
    );
}
